import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DialogResult, PopupDialog } from '@app/Common';
import { ProvisionedApplicationsTypes } from '@app/Enums';
import { Tenant } from '@app/Models/tenant';
import { TenantEnvironmentDetails } from '@app/Models/tenant-environment-details.model';
import { TenantService } from '@app/Services';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/internal/operators/filter';
import { map } from 'rxjs/internal/operators/map';
import { CopyConfigService } from '../../Services';
import {takeUntil} from "rxjs/operators";
import { AsyncPipe } from '@angular/common';
import { JobStatusPlaceholderComponent } from '../../../../../Common/job-status-placeholder/job-status-placeholder.component';
import { MatButton, MatIconButton } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatFormField, MatLabel, MatInput, MatSuffix } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';

@Component({
    selector: 'app-copy-config-import-only',
    templateUrl: './copy-config-import-only.component.html',
    styleUrls: [
        './copy-config-import-only.component.scss'
    ],
    imports: [JobStatusPlaceholderComponent, MatButton, FormsModule, MatSelect, MatOption, MatFormField, MatLabel, MatInput, MatIconButton, MatSuffix, MatIcon, MatProgressBar, AsyncPipe]
})
export class CopyConfigImportOnlyComponent implements OnInit, OnDestroy {
  copyConfigInProgress$: Observable<boolean>;
  copyConfigFinalised$: Observable<boolean>;
  copyConfigJobId$: Observable<string>;
  copyToTenantValue: string;
  copyToTenant: Tenant;
  copyToTenant$: Observable<Tenant>;
  copyToEnvironment$: Observable<string>;
  importId: string;
  selectedFile: File | null = null;
  uploadProgress: number | null = null;
  isUploading: boolean = false;

  private isDestroyed$ = new Subject<void>();

  constructor(
    private tenantService: TenantService,
    private copyConfigService: CopyConfigService,
    private readonly dialog: MatDialog,
    private readonly http: HttpClient
  ) {
    this.copyConfigInProgress$ = this.copyConfigService.copyConfigInProgress$.asObservable();
    this.copyConfigFinalised$ = this.copyConfigService.copyConfigFinalised$.asObservable();
    this.copyConfigJobId$ = this.copyConfigService.copyConfigJobId$.asObservable();
    this.copyToTenant$ = this.copyConfigService.targetTenant$.asObservable();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
    } else {
      this.selectedFile = null;
    }
  }

  uploadFile(): void {
    if (!this.selectedFile) {
      return;
    }
    const formData = new FormData();
    formData.append('file', this.selectedFile);
    this.isUploading = true;
    this.uploadProgress = 0;
    this.http.post(`api/tenant/copyconfig/import/upload?tenantId=${this.copyToTenant.TenantId}`, formData, {
      reportProgress: true,
      observe: 'events'
    })
    .pipe(takeUntil(this.isDestroyed$))
    .subscribe({
      next: (event: HttpEvent<any>) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          this.uploadProgress = Math.round(100 * event.loaded / event.total);
        } else if (event.type === HttpEventType.Response) {
          this.isUploading = false;
          this.uploadProgress = null;
          console.log('File uploaded successfully', event.body);

          this.importId = event.body;
        }
      },
      error: (err) => {
        this.isUploading = false;
        this.uploadProgress = null;
        console.error('File upload failed', err);
      }
    });
  }

  ngOnInit(): void {
    this.setDefaultTenantFromValue();
    this.changeCopyTo();
  }

  ngOnDestroy(): void {
    this.isDestroyed$.next();
  }

  changeCopyTo(): void {
    if (!this.copyToTenantValue) {
      return;
    }

    this.copyToTenant = this.getCopyToTenantOptions().find(
      (tenant) => tenant.Value === this.copyToTenantValue
    );
    this.copyToEnvironment$ = this.getEnvironmentForTenant(
      this.copyToTenantValue
    );

    this.copyConfigService.loadTenantModuleConfigOptionsExportOnly(this.copyToTenantValue);
  }

  copyAnotherTenant(): void {
    location.reload();
  }

  getCopyToTenantOptions(): Tenant[] {
    return this.tenantService.tenants.filter((tenant) =>
      tenant.ProvisionedApplications.includes(ProvisionedApplicationsTypes.Airsweb)
    );
  }

  copyConfigConfirm(): void {
    const dialogRef =
      this.dialog.open<PopupDialog, any, DialogResult>(PopupDialog);
    dialogRef.componentInstance.DialogTitle = 'Copy Configuration Import Only';
    dialogRef.componentInstance.DialogDescription =
      `Are you sure you want to Copy Config <b>Import Only</b> to Tenant <b>${this.copyToTenantValue}</b>?<br><br>` +
      `This will overwrite the imported modules' draft configuration on the destination tenant, discarding any unpublished changes there.`;
    dialogRef.componentInstance.DialogConfirmButtonText = 'Confirm';
    dialogRef.componentInstance.DialogCancelButtonText = 'Cancel';

    dialogRef.afterClosed().subscribe((result) => {
      if (result !== undefined && result.Result) {
        this.copyConfigService.copyConfigInProgress$.next(true);
        this.copyConfig();
      } else {
        this.copyConfigService.copyConfigInProgress$.next(false);
      }
    });
  }

  copyConfig(): void {
    this.copyConfigService.targetTenant$.next(this.copyToTenant);
    this.copyConfigService.copyConfigImportOnlyRequest(this.copyToTenantValue, this.importId)
      .subscribe((jobId) => {
        this.copyConfigService.copyConfigJobId$.next(jobId);
      });
  }

  isImportIdValid(): boolean {
    return !!this.importId && this.importId.length === 36;
  }

  isCopyConfigAvailable(): boolean {
    return !!this.copyToTenantValue;
  }

  private getEnvironmentForTenant(tenantName: string): Observable<string> {
    return this.tenantService
      .getEnvironmentDetailsByTenant(tenantName)
      .pipe(
        filter((environemntDetails: TenantEnvironmentDetails | null) => environemntDetails !== null),
        map((environemntDetails: TenantEnvironmentDetails) =>
          environemntDetails.EnvironmentName
        )
      );
  }

  private setDefaultTenantFromValue(): void {
    if (!this.tenantService.selectedTenant) {
      return;
    }
    this.copyToTenant = this.tenantService.selectedTenant;
    this.copyToTenantValue = this.copyToTenant.Value;
  }
}


